import { useState } from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { type DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface DateRangePickerProps {
  value: DateRange | undefined;
  onSelect: (range: DateRange | undefined) => void;
  placeholder?: string;
  id?: string;
  className?: string;
}

/**
 * Date-range picker: a button that opens a two-month calendar in a popover.
 * The popover closes itself once both endpoints are chosen; callers can treat
 * a range with `from` and `to` set as a complete selection.
 */
export function DateRangePicker({
  value,
  onSelect,
  placeholder = "Pick a date range",
  id,
  className,
}: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const label = rangeLabel(value, placeholder);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          variant="outline"
          className={cn(
            "justify-start text-left font-normal",
            !value?.from && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="range"
          selected={value}
          defaultMonth={value?.from}
          numberOfMonths={2}
          onSelect={(range) => {
            onSelect(range);
            if (range?.from && range?.to) {
              setOpen(false);
            }
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Builds the trigger label for the current selection state.
 */
function rangeLabel(value: DateRange | undefined, placeholder: string): string {
  if (!value?.from) {
    return placeholder;
  }
  const from = format(value.from, "MMM d, yyyy");
  if (!value.to) {
    return `${from} – …`;
  }
  return `${from} – ${format(value.to, "MMM d, yyyy")}`;
}
